import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Upload, X, Loader2, Film, Image as ImageIcon, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Honest media picker.
 * - Each file gets its own row while it uploads: spinner → tick or red error message.
 * - Nothing is added to the parent images/video list until the server returns a URL.
 * - Fires `onBusyChange` so the parent can disable "Publish" while any upload is in flight.
 * - Shows recommended dimensions/size limits inline.
 */
const MediaPicker = ({
  images = [],
  setImages,
  video,
  setVideo,
  maxImages = 4,
  allowVideo = true,
  testId = "media-picker",
  imageHint = "1200 × 1200 px · square (1:1) · JPG/PNG/WebP · ≤ 8 MB",
  videoHint = "1080 × 1080 or 1920 × 1080 · MP4/WebM · ≤ 50 MB · ≤ 30 s",
  onBusyChange,
}) => {
  const [dragging, setDragging] = useState(false);
  const [pending, setPending] = useState([]); // [{id, name, kind, status, error}]
  const inputRef = useRef(null);

  const busy = pending.some((p) => p.status === "uploading");
  useEffect(() => { if (onBusyChange) onBusyChange(busy); }, [busy, onBusyChange]);

  const setRow = (id, patch) =>
    setPending((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const upload = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    const list = Array.from(files);

    // Snapshot counts + local accumulator so we don't rely on parent re-renders inside the loop.
    let imgCount = images.length;
    let videoSet = !!video;
    let localImages = [...images];
    let localVideo = video;

    for (const file of list) {
      const isVideo = file.type.startsWith("video/");
      const isImage = file.type.startsWith("image/");
      const rowId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      if (!isVideo && !isImage) {
        setPending((p) => [...p, { id: rowId, name: file.name, kind: "unknown", status: "error", error: "Unsupported file type" }]);
        toast.error(`${file.name}: only images or MP4/WebM videos are allowed`);
        continue;
      }
      if (isVideo && !allowVideo) {
        setPending((p) => [...p, { id: rowId, name: file.name, kind: "video", status: "error", error: "Video not allowed here" }]);
        toast.error(`${file.name}: video not allowed here`);
        continue;
      }
      if (isVideo && videoSet) {
        setPending((p) => [...p, { id: rowId, name: file.name, kind: "video", status: "error", error: "Only 1 video allowed — remove existing first" }]);
        toast.error("Only 1 video allowed — remove the existing one first");
        continue;
      }
      if (isImage && imgCount >= maxImages) {
        setPending((p) => [...p, { id: rowId, name: file.name, kind: "image", status: "error", error: `Max ${maxImages} images` }]);
        toast.error(`${file.name}: max ${maxImages} images reached`);
        continue;
      }

      if (isVideo) videoSet = true; else imgCount += 1;

      setPending((p) => [...p, { id: rowId, name: file.name, kind: isVideo ? "video" : "image", status: "uploading" }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (!data?.url) throw new Error("Upload succeeded but no URL was returned");
        if (data.kind === "video") {
          localVideo = data.url;
          setVideo(data.url);
        } else {
          localImages = [...localImages, data.url];
          setImages(localImages);
        }
        setRow(rowId, { status: "done" });
        // Auto-clear the completed row after a short beat so the list stays tidy.
        setTimeout(() => setPending((prev) => prev.filter((p) => p.id !== rowId)), 1500);
      } catch (err) {
        // Roll back the projected count so subsequent files aren't wrongly rejected.
        if (isVideo) videoSet = false; else imgCount -= 1;
        const msg = formatError(err);
        setRow(rowId, { status: "error", error: msg });
        toast.error(`${file.name}: ${msg}`);
      }
    }
    // Clear the file input so re-selecting the same file re-uploads.
    if (inputRef.current) inputRef.current.value = "";
  }, [images, video, setImages, setVideo, maxImages, allowVideo]);

  const onDrop = (e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files); };
  const canAddImage = images.length < maxImages;
  const canAddVideo = allowVideo && !video;

  return (
    <div data-testid={testId}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${dragging ? "border-[#2D5A47] bg-[#EBF2EE]" : "border-[#E2DCD2] bg-white hover:border-[#2D5A47]"}`}
      >
        <input
          ref={inputRef}
          id={`${testId}-file`}
          type="file"
          accept={allowVideo ? "image/*,video/mp4,video/webm,video/quicktime" : "image/*"}
          multiple
          className="hidden"
          onChange={(e) => upload(e.target.files)}
          data-testid={`${testId}-input`}
        />
        <div className="flex flex-col items-center gap-2 text-[#64748B]">
          {busy ? (
            <Loader2 className="animate-spin text-[#2D5A47]" size={24} />
          ) : (
            <Upload size={24} className="text-[#2D5A47]" />
          )}
          <div className="text-sm">
            <span className="font-semibold text-[#1E2A24]">
              {busy ? "Uploading…" : `Drop images${allowVideo ? " or a video" : ""}`}
            </span>
            {!busy && <> or click to browse</>}
          </div>
          <div className="text-xs leading-relaxed">
            <div><b>Image:</b> {imageHint} · up to {maxImages}</div>
            {allowVideo && <div className="mt-0.5"><b>Video:</b> {videoHint} · 1 file</div>}
            <div className="mt-1 text-[#8C6D46]">
              {images.length}/{maxImages} images{allowVideo ? ` · ${video ? "1/1" : "0/1"} video` : ""}
            </div>
          </div>
        </div>
      </div>

      {(images.length > 0 || video) && (
        <div className="mt-3 grid grid-cols-4 gap-2">
          {images.map((url, i) => (
            <div key={`img-${i}`} className="relative group aspect-square rounded-lg overflow-hidden border border-[#E2DCD2] bg-[#F4F1EA]" data-testid={`${testId}-image-${i}`}>
              <img src={url} alt="" className="w-full h-full object-cover" />
              <div className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1"><ImageIcon size={10} /> {i + 1}</div>
              <button type="button" onClick={() => setImages(images.filter((_, k) => k !== i))} className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition" data-testid={`${testId}-image-remove-${i}`}>
                <X size={12} />
              </button>
            </div>
          ))}
          {video && (
            <div className="relative group aspect-square rounded-lg overflow-hidden border-2 border-[#C86D42] bg-black" data-testid={`${testId}-video`}>
              <video src={video} muted className="w-full h-full object-cover" />
              <div className="absolute top-1 left-1 bg-[#C86D42] text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1"><Film size={10} /> Video</div>
              <button type="button" onClick={() => setVideo(null)} className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition" data-testid={`${testId}-video-remove`}>
                <X size={12} />
              </button>
            </div>
          )}
        </div>
      )}

      {pending.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid={`${testId}-status`}>
          {pending.map((row) => (
            <div
              key={row.id}
              className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg border ${row.status === "error" ? "bg-red-50 border-red-200 text-red-700" : row.status === "done" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-[#F4F1EA] border-[#E2DCD2] text-[#1E2A24]"}`}
              data-testid={`${testId}-row-${row.status}`}
            >
              {row.status === "uploading" && <Loader2 size={12} className="animate-spin" />}
              {row.status === "error" && <AlertCircle size={12} />}
              {row.status === "done" && <span className="text-emerald-600">✓</span>}
              <span className="font-medium truncate max-w-[60%]">{row.name}</span>
              <span className="ml-auto">
                {row.status === "uploading" && "Uploading…"}
                {row.status === "done" && "Uploaded"}
                {row.status === "error" && (row.error || "Upload failed")}
              </span>
              {row.status === "error" && (
                <button
                  type="button"
                  onClick={() => setPending((prev) => prev.filter((p) => p.id !== row.id))}
                  className="ml-2 text-red-600 hover:text-red-800"
                  aria-label="Dismiss"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!canAddImage && !canAddVideo && (
        <div className="mt-2 text-xs text-[#8C6D46]">Maximum media added. Remove one to upload another.</div>
      )}
    </div>
  );
};

export default MediaPicker;
